// src/pages/MesasPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { getMesas, createMesa, updateMesa, deleteMesa } from "../api/mesas.js";
import { getRestaurantes } from "../api/restaurantes.js";
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { useAuth } from "../context/AuthContext.js";
import { usePagination } from "../hooks/usePagination.js";
import Pagination from "../components/Pagination";

const MesasPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [mesas, setMesas] = useState([]);
    const [mesa, setMesa] = useState({ numero_mesa: "", capacidad: "", estado: "disponible", id_restaurante: "", });
    const [filteredMesas, setFilteredMesas] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [restaurantes, setRestaurantes] = useState([]);
    const inputRefs = useRef({});
    const { sortedItems: sortedMesas, sortConfig, setSortConfig } = useSortableData(filteredMesas);
    const { currentPage, setCurrentPage, paginatedItems: currentMesas, totalPages, itemsPerPage } = usePagination(sortedMesas, 20);
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    // Cargar restaurantes
    useEffect(() => {
        const loadRestaurantes = async () => {
        try {
            const data = await getRestaurantes();
            setRestaurantes(Array.isArray(data) ? data : data.restaurantes || []);
        } catch (err) {
            console.error("Error al cargar restaurantes:", err);
        }
        };
        loadRestaurantes();
    }, []);

    // Cargar mesas
    const loadMesas = async () => {
        try {
        const data = await getMesas();
        const lista = Array.isArray(data) ? data : data.mesas || [];
        setMesas(lista);
        setFilteredMesas(lista);
        } catch (err) {
        console.error("Error al cargar mesas:", err);
        }
    };

    useEffect(() => {
        loadMesas();
    }, []);

    // Filtro de búsqueda
    useEffect(() => {
        const filtered = mesas.filter((m) =>
        Object.values(m).some((val) =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
        );
        setFilteredMesas(filtered);
    }, [searchTerm, mesas]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setMesa({ ...mesa, [name]: value });
    };

    const resetForm = () => {
        setMesa({
        numero_mesa: "",
        capacidad: "",
        estado: "disponible",
        id_restaurante: usuarioLogueado?.isSuperAdmin ? "" : usuarioLogueado?.id_restaurante || "",
        });
        setErrors({});
        setEditMode(false);
    };

    const validateMesa = (data) => {
        const newErrors = {};
        if (!data.numero_mesa || isNaN(data.numero_mesa))
        newErrors.numero_mesa = "Ingrese un número de mesa válido.";
        if (!data.capacidad || isNaN(data.capacidad))
        newErrors.capacidad = "Ingrese una capacidad válida.";
        if (!data.id_restaurante)
        newErrors.id_restaurante = "Seleccione un restaurante.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateMesa(mesa);
        if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        const firstErrorField = Object.keys(validationErrors)[0];
        const fieldRef = inputRefs.current[firstErrorField];
        if (fieldRef?.scrollIntoView) {
            fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
            fieldRef.focus();
        }
        return;
        }

        try {
        const mesaData = { ...mesa };

        if (!usuarioLogueado?.isSuperAdmin) {
            mesaData.id_restaurante = usuarioLogueado?.id_restaurante;
        }

        if (editMode) {
            await updateMesa(mesa.id_mesa, mesaData);
            alert("Mesa actualizada correctamente");
        } else {
            await createMesa(mesaData);
            alert("Mesa creada correctamente");
        }
        resetForm();
        setModalOpen(false);
        loadMesas();
        } catch (err) {
        console.error("Error del backend:", err.response?.data || err.message);
        const backendMsg =
            err.response?.data?.error ||
            err.response?.data?.message ||
            "Error al guardar la mesa.";
        setErrors({ general: backendMsg });
        alert(backendMsg);
        }
    };

    const handleEdit = (m) => {
        setMesa(m);
        setEditMode(true);
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar esta mesa?")) {
        await deleteMesa(id);
        loadMesas();
        }
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-center">Gestión de Mesas</h1>

        {/* Buscador + botón */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <input type="text" placeholder="🔍 Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
            <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700" > ➕ Nueva Mesa </button>
        </div>

        {/* Tabla */}
        <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
            <table className="min-w-full text-sm text-left divide-y divide-gray-200">
            <thead className="bg-gray-100 text-gray-700 font-semibold">
                <tr>
                <SortableHeader label="N°" sortKey="id_mesa" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Número de Mesa" sortKey="numero_mesa" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Capacidad" sortKey="capacidad" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                {usuarioLogueado?.isSuperAdmin && (
                    <SortableHeader label="Restaurante" sortKey="restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                )}
                <th className="px-4 py-2 text-center">Acciones</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
                {currentMesas.length > 0 ? (
                currentMesas.map((m, idx) => (
                    <tr key={m.id_mesa} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                    <td className="px-4 py-2">{m.numero_mesa}</td>
                    <td className="px-4 py-2">{m.capacidad}</td>
                    <td className="px-4 py-2">{m.estado}</td>
                    {usuarioLogueado?.isSuperAdmin && (
                        <td className="px-4 py-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                            {m.restaurante?.nombre_comercial || "Sin restaurante"}
                        </span>
                        </td>
                    )}
                    <td className="px-4 py-2 text-center flex justify-center gap-2">
                        <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(m)}> Editar </button>
                        <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(m.id_mesa)}> Eliminar </button>
                    </td>
                    </tr>
                ))
                ) : (
                <tr>
                    <td colSpan={usuarioLogueado?.isSuperAdmin ? "6" : "5"} className="text-center py-4 text-gray-500"> No hay mesas registradas </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>

        {/* 🔹 Paginación */}
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

        {/* Modal Crear / Editar */}
        {modalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg w-[70%] max-h-[90vh] overflow-y-auto px-10 py-6 relative">
                <button onClick={() => setModalOpen(false)} className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl"> ✖ </button>
                <h2 className="text-2xl font-semibold mb-6 text-center"> {editMode ? "Editar Mesa" : "Nueva Mesa"} </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputGroup label="Número de Mesa" error={errors.numero_mesa}> <input name="numero_mesa" value={mesa.numero_mesa} onChange={handleChange} placeholder="Ej: 5" className="input-base" ref={(el) => (inputRefs.current["numero_mesa"] = el)} /> </InputGroup>
                    <InputGroup label="Capacidad" error={errors.capacidad}> <input name="capacidad" value={mesa.capacidad} onChange={handleChange} placeholder="Ej: 4" className="input-base" ref={(el) => (inputRefs.current["capacidad"] = el)} /> </InputGroup>
                    <InputGroup label="Estado" error={errors.estado}> <select name="estado" value={mesa.estado} onChange={handleChange} className="input-base" > <option value="disponible">Disponible</option> <option value="reservada">Reservada</option> <option value="ocupada">Ocupada</option> </select> </InputGroup>
                    {usuarioLogueado?.isSuperAdmin && (
                    <InputGroup label="Restaurante" error={errors.id_restaurante}>
                        <select name="id_restaurante" value={mesa.id_restaurante} onChange={handleChange} className="input-base" >
                        <option value="">Seleccione un restaurante</option>
                        {restaurantes.map((r) => (
                            <option key={r.id_restaurante} value={r.id_restaurante}> {r.nombre_comercial} </option>
                        ))}
                        </select>
                    </InputGroup>
                    )}
                </div>

                {errors.general && (
                    <p className="text-red-600 text-sm text-center">{errors.general}</p>
                )}

                <div className="text-center">
                    <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"> {editMode ? "Actualizar" : "Guardar"} </button>
                </div>
                </form>
            </div>
            </div>
        )}
        </div>
    );
};

export default MesasPage;
